<script lang="ts">
export default defineComponent({
  inheritAttrs: false,
})
</script>
<script setup lang="ts">
import { computed, defineComponent } from 'vue'
import { useAttrs } from 'vue'

const attrs = useAttrs()

interface Props {
  modelValue: boolean | Array<any> | Set<any>
  disabled?: boolean
  label?: string
  smallText?: boolean
}
interface Emit {
  (emit: 'update:modelValue', value: boolean | Array<any> | Set<any>): void
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  smallText: false,
})
const emit = defineEmits<Emit>()

const modelValueProxy = computed<boolean | Array<any> | Set<any>>({
  get() {
    const { modelValue } = props
    const { value } = attrs

    if (typeof modelValue === 'boolean') {
      return modelValue
    } else if (Array.isArray(modelValue)) {
      return modelValue.includes(value)
    } else {
      return modelValue.has(value)
    }
  },
  set(value) {
    const { modelValue } = props
    const { value: currentValue } = attrs

    if (typeof modelValue === 'boolean') {
      emit('update:modelValue', value as boolean)
    } else if (Array.isArray(modelValue)) {
      const newArray = modelValue.includes(currentValue)
        ? modelValue.filter((el) => el !== currentValue)
        : [...modelValue, currentValue]

      emit('update:modelValue', newArray)
    } else {
      const newSet = new Set(modelValue)
      newSet.has(currentValue) ? newSet.delete(currentValue) : newSet.add(currentValue)
      emit('update:modelValue', newSet)
    }
  },
})
</script>

<template>
  <label class="checkbox" @click.stop>
    <input
      v-model="modelValueProxy"
      v-bind="$attrs"
      :disabled="disabled"
      type="checkbox"
      class="checkbox__input"
    />
    <span class="checkbox__box" :class="{ checkbox__box_disabled: disabled }" />
    <span
      v-if="$slots.label"
      class="checkbox__text"
      :class="{
        checkbox__text_checked: modelValue,
        checkbox__text_sm: smallText,
      }"
    >
      <slot name="label" />
    </span>
  </label>
</template>

<style scoped lang="scss">
.checkbox {
  display: flex;
  align-items: start;
  position: relative;
  cursor: pointer;
  min-height: max-content;
  width: 100%;

  @media (hover: hover) {
    &:hover {
      .checkbox__box {
        border-color: $primary-green;
      }

      .checkbox__text {
        color: $primary-green;
      }
    }
  }

  &:active {
    .checkbox__box {
      border-color: $primary-green;
    }
  }

  &__text {
    margin-left: 1.5rem;
    @include h4-6;
    color: $primary-text;
    transition: color 0.15s ease-in-out;
    height: max-content;

    &_checked {
      color: $primary-text;
    }

    &_sm {
      @include h4-8;
    }
  }

  > input.checkbox__input {
    position: absolute;
    z-index: -1;
    opacity: 0;
    padding: 0;
  }

  > .checkbox__box {
    border: 1px solid $primary-green;
    border-radius: 0.125rem;
    background: 0 0;
    position: absolute;
    left: 0;
    height: 1rem;
    width: 1rem;
    transition: border-color 0.15s ease-in-out;

    &:after {
      content: '';
      position: absolute;
      display: none;
      top: -1px;
      left: -1px;
      width: 1rem;
      height: 1rem;
      background-image: url('@caks/shared/assets/img/checked.svg');
      border: none;
      animation: fadeIn 0.15s ease-in-out;
      border-radius: 0.125rem;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      scale: 0;
    }
    to {
      opacity: 1;
      scale: 1;
    }
  }

  > input.checkbox__input:checked ~ .checkbox__box:after {
    display: block;
  }
}
</style>
